<template>
    <VButton
        v-if="displayButton"
        class="print-order-receipt-btn primary rounded"
        :class="{ loading: isLoading, hidden: isButtonHidden }"
        :disabled="isDisabled"
        @click="printOrderReceipt"
    >
        {{ translations.receipt.printReceipt }}
    </VButton>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { PrintDao } from "@/data/dao/service/PrintDao";
    import { exceptionTypes } from "@/data/util/Config";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    // Props definition
    const props = defineProps<{
        orderId: string;
        locationId: string;
        onStatusUpdated?: (isPrintAvailable: boolean) => void;
        onPrintSuccess?: () => void;
        hidden?: boolean;
        hideIfNotAvailable?: boolean;
    }>();

    // State
    const printDao = Factory.getInstance<PrintDao>(PrintDao);
    const isPrintReceiptAvailable = ref(false);
    const isLoading = ref(false);

    // Computed properties
    const displayButton = computed(() => (props.hideIfNotAvailable ? isPrintReceiptAvailable.value : true));
    const isButtonHidden = computed(() => props.hidden ?? false);
    const isDisabled = computed(() => isLoading.value || !isPrintReceiptAvailable.value);

    // Methods
    async function printOrderReceipt() {
        if (isLoading.value) return;

        if (!isPrintReceiptAvailable.value) {
            Popup.show({ message: "Print is not enabled" });
            return;
        }

        try {
            isLoading.value = true;
            await printDao.printOrderReceipt(props.orderId, { locationId: props.locationId });

            if (props.onPrintSuccess) {
                props.onPrintSuccess();
            }
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;

            if (e.type === exceptionTypes.noProductionSiteForRoomEnabledException) {
                Popup.show({ message: translations.receipt.noProductionSiteEnabledForRoom });
            } else {
                Popup.show({ message: translations.receipt.failedPrintReceipt });
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function checkIsPrintReceiptAvailable() {
        try {
            const result = await printDao.getIsPrintOrderReceiptAvailable({
                orderId: props.orderId,
                locationId: props.locationId,
            });

            isPrintReceiptAvailable.value = result.isPrintReceiptAvailable;

            if (props.onStatusUpdated) {
                props.onStatusUpdated(isPrintReceiptAvailable.value);
            }
        } catch (ex) {
            logError(ex);
        }
    }

    // Check print receipt availability on component mount
    onMounted(checkIsPrintReceiptAvailable);
</script>

<style lang="scss">
    .print-order-receipt-btn.v-btn.primary {
        font-size: 17px;
        margin-top: 0.625em;
        height: 2.75em;
        line-height: 2.75em;
        padding: 0 2em;
        border-radius: 1.5em !important;
        width: 100%;

        &.hidden {
            display: none;
        }
    }
</style>
